import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle2, Sparkles, TrendingUp, Zap, Target, Shield } from 'lucide-react';

export const Home = () => {
  const features = [
    {
      icon: Target,
      title: 'GEO / AIO',
      description: 'Optimize your content, structure, and authority so ChatGPT, Perplexity, and Google AI choose you as a source.',
      link: '/services/geo-aio'
    },
    {
      icon: Zap,
      title: 'AI Ads',
      description: 'Prepare and manage your brand\'s presence in ad placements inside AI tools and AI-powered surfaces.',
      link: '/services/ai-ads'
    },
    {
      icon: Sparkles,
      title: 'AI Agents & Automations',
      description: 'Build automations for content and programmatic SEO pages around trending GEO-aligned keywords, so your brand shows up in more AI answers.',
      link: '/services/ai-agents'
    }
  ];

  const featureIconImages = [
    '/assets/ai-search.png',
    '/assets/ai-ads.png',
    '/assets/automation.png'
  ];

  const stats = [
    { label: 'AI Engines Covered', value: '10+' },
    { label: 'Citations Improved', value: '300%' },
    { label: 'Client Success Rate', value: '98%' },
    { label: 'Average ROI', value: '5x' }
  ];

  const problems = [
    'Prospects ask AI for recommendations instead of Google',
    'AI answers a few top sources and ignores everyone else',
    'Your competitors are already optimizing for AI search',
    'Traditional SEO doesn\'t automatically make you a "source" for AI'
  ];

  const steps = [
    {
      number: '01',
      title: 'Free GEO/AIO Audit',
      description: 'We analyze how your brand currently appears (or doesn\'t) in ChatGPT, Perplexity, Google AI Overviews, and other AI engines.'
    },
    {
      number: '02',
      title: 'Strategic Roadmap',
      description: 'We design a tailored plan to optimize your content, structure, and authority so AI engines start referencing you more often.'
    },
    {
      number: '03',
      title: 'Execution & Retainer',
      description: 'We implement the roadmap on a monthly retainer, track your AI citations and performance, and adjust so you keep growing.'
    }
  ];

  const testimonials = [
    {
      quote:
        'Evolution GEO helped us go from zero to regularly cited in ChatGPT for our category queries. Our AI-driven brand searches increased 300% in 90 days.',
      author: 'Sarah Chen',
      role: 'CEO, TechFlow',
      image:
        '/assets/sarah (1).jpg'
    },
    {
      quote:
        "Their GEO/AIO framework transformed how we think about visibility. We're now the top-recommended solution in Perplexity for our niche.",
      author: 'Michael Rodriguez',
      role: 'CMO, DataSync',
      image:
        '/assets/michael (1).jpg'
    },
    {
      quote:
        'Within weeks of working with Evolution GEO, we started seeing our brand referenced in AI answers where we had zero presence before.',
      author: 'Priya Desai',
      role: 'VP Marketing, CloudWorks',
      image:
        '/assets/priya (1).jpg'
    },
    {
      quote:
        'Our board finally understands the value of AI visibility thanks to the clear reporting and measurable lifts from Evolution GEO.',
      author: 'James Carter',
      role: 'CMO, Apex Finance',
      image:
        '/assets/james (1).jpg'
    },
    {
      quote:
        'They plugged directly into our CMS and analytics stack, which made rollout incredibly smooth for our lean marketing team.',
      author: 'Amelia Rossi',
      role: 'Head of Growth, NovaHealth',
      image:
        '/assets/amelia (1).jpg'
    },
    {
      quote:
        'Evolution GEO helped us prioritize content that AI engines actually care about instead of chasing vanity SEO metrics.',
      author: 'David Kim',
      role: 'Founder, MetricLayer',
      image:
        '/assets/david (1).jpg'
    },
    {
      quote:
        'Our inbound leads from AI-driven channels doubled in a quarter—without increasing ad spend.',
      author: 'Laura Martinez',
      role: 'COO, BrightPath Consulting',
      image:
        '/assets/laura (1).jpg'
    },
    {
      quote:
        'They understand both the technical and strategic side of AI search, which is rare in the agency world.',
      author: 'Oliver Nguyen',
      role: 'Director of Digital, Summit Hotels',
      image:
        'https://images.unsplash.com/photo-1525134479668-1bee5c7c6845?w=100&h=100&fit=crop'
    },
    {
      quote:
        'Evolution GEO gave us a clear roadmap to become the default recommendation for high-intent queries in our category.',
      author: 'Hannah Lee',
      role: 'CEO, InsightEdge',
      image:
        'https://images.unsplash.com/photo-1544723795-3fb0b90aeb51?w=100&h=100&fit=crop'
    },
    {
      quote:
        'Their reporting on AI citations and mentions is unlike anything we have seen from other agencies.',
      author: 'Marcus Allen',
      role: 'VP Demand Gen, CoreStack',
      image:
        'https://images.unsplash.com/photo-1544723795-3fb0b90aeb77?w=100&h=100&fit=crop'
    },
    {
      quote:
        'We finally feel ahead of the curve in AI search instead of reacting to every algorithm change.',
      author: 'Chloe Dubois',
      role: 'CMO, Skyline Retail',
      image:
        'https://images.unsplash.com/photo-1544723795-3fb0b90aeb88?w=100&h=100&fit=crop'
    },
    {
      quote:
        'Evolution GEO has become a strategic partner, not just another vendor—they sit at the table for our biggest growth decisions.',
      author: 'Noah Fischer',
      role: 'Chief Strategy Officer, HelioLabs',
      image:
        'https://images.unsplash.com/photo-1544723795-3fb0b90aeb99?w=100&h=100&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 gradient-hero opacity-5" />
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1597733336794-12d05021d510?w=1920&h=1080&fit=crop)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
              <Sparkles className="w-3 h-3 mr-1" />
              Trusted by forward-thinking brands
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
              Get Your Brand Referenced by{' '}
              <span className="text-gradient">ChatGPT</span>,{' '}
              <span className="text-gradient">Perplexity</span>, and{' '}
              <span className="text-gradient">Google AI</span>{' '}
              Overviews
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              We help future-proof brands become the default answer in AI-driven search—through Generative Engine Optimization (GEO), AI ads, and AI agents.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Link to="/free-audit">
                <Button
                  size="lg"
                  className="bg-primary text-primary-foreground shadow-primary hover:bg-primary/90 transition-all"
                >
                  Get My Free GEO/AIO Audit
                  <span className="ml-2 text-sm sm:text-base">
                    – Usually <span className="font-semibold">$2,000+</span>
                  </span>
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/5 hover:text-primary hover:border-primary"
                >
                  Book a Discovery Call
                </Button>
              </Link>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground">
              <span>Trusted by growing brands • Certified in AIO &amp; GEO by</span>
              <div className="flex items-center gap-4 opacity-80">
                <img
                  src="/assets/ibm.png"
                  alt="IBM"
                  className="h-6 w-auto"
                  loading="lazy"
                />
                <img
                  src="/assets/google.webp"
                  alt="Google"
                  className="h-6 w-auto"
                  loading="lazy"
                />
                <img
                  src="/assets/microsoft.webp"
                  alt="Microsoft"
                  className="h-6 w-auto"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-display font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Strip */}
      <section className="py-12 border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-muted-foreground mb-8">
            Optimizing for the AI engines your customers use daily
          </p>
          <div className="flex flex-wrap justify-center items-center gap-10 opacity-80">
            <img
              src="/assets/chatgpt.png"
              alt="ChatGPT"
              className="h-10 w-auto object-contain"
              loading="lazy"
            />
            <img
              src="/assets/perplexity.png"
              alt="Perplexity"
              className="h-10 w-auto object-contain"
              loading="lazy"
            />
            <img
              src="/assets/claude.png"
              alt="Claude"
              className="h-8 md:h-10 w-auto object-contain"
              loading="lazy"
            />
            <img
              src="/assets/Google_AI_Logo_2018.svg"
              alt="Google AI"
              className="h-10 w-auto object-contain"
              loading="lazy"
            />
            <img
              src="/assets/Google_Gemini_Logo_2025.svg"
              alt="Gemini"
              className="h-8 md:h-10 w-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6">
              Your SEO Is No Longer Enough
            </h2>
            <p className="text-lg text-muted-foreground">
              Every day, millions of people ask ChatGPT, Perplexity, Claude, Google's AI Overviews, and other AI tools instead of scrolling through traditional search results. If your brand isn't cited, recommended, or referenced in those answers, you're invisible—even if you rank #1 in Google.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {problems.map((problem, index) => (
              <Card key={index} className="border-border hover:border-primary/50 transition-colors">
                <CardContent className="flex items-start space-x-4 pt-6">
                  <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-destructive font-bold">!</span>
                  </div>
                  <p className="text-foreground">{problem}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6">
              We Turn Your Brand Into an{' '}
              <span className="text-gradient">AI-Referenced Authority</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our GEO/AIO framework is designed to make your brand show up in AI answers, AI overviews, and AI-powered discovery—then turn that visibility into leads and customers.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all hover:border-primary/50">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center mb-4 group-hover:shadow-primary transition-shadow">
                    <img
                      src={featureIconImages[index]}
                      alt={feature.title}
                      className="w-8 h-8 object-contain"
                      loading="lazy"
                    />
                  </div>
                  <CardTitle className="font-display">{feature.title}</CardTitle>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={feature.link}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="transition-colors group-hover:bg-primary group-hover:text-primary-foreground"
                    >
                      Learn more
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6">
              How We Help You Win in AI Search
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary/50 to-transparent" />
                )}
                <div className="relative">
                  <div className="text-6xl font-display font-bold text-primary/20 mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-display font-semibold mb-3">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-muted/30 overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="space-y-8">
            {/* Top row */}
            <div className="relative">
              <div className="flex gap-6 animate-testimonials-left">
                {[...testimonials, ...testimonials].map((testimonial, index) => (
                  <Card
                    key={`top-${index}`}
                    className="w-80 flex-shrink-0 border-border/60 bg-card/80 shadow-md rounded-3xl"
                  >
                    <CardContent className="py-5 px-6 flex flex-col h-full">
                      <div className="text-2xl text-primary mb-2 leading-none">“</div>
                      <p className="text-sm text-foreground mb-4 flex-1">
                        {testimonial.quote}
                      </p>
                      <div className="flex items-center space-x-3">
                        <img
                          src={testimonial.image}
                          alt={testimonial.author}
                          className="w-9 h-9 rounded-full object-cover border-2 border-primary"
                        />
                        <div>
                          <div className="text-sm font-semibold">
                            {testimonial.author}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {testimonial.role}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Bottom row */}
            <div className="relative">
              <div className="flex gap-6 animate-testimonials-right">
                {[...testimonials, ...testimonials].map((testimonial, index) => (
                  <Card
                    key={`bottom-${index}`}
                    className="w-80 flex-shrink-0 border-border/60 bg-card/80 shadow-md rounded-3xl"
                  >
                    <CardContent className="py-5 px-6 flex flex-col h-full">
                      <div className="text-2xl text-primary mb-2 leading-none">“</div>
                      <p className="text-sm text-foreground mb-4 flex-1">
                        {testimonial.quote}
                      </p>
                      <div className="flex items-center space-x-3">
                        <img
                          src={testimonial.image}
                          alt={testimonial.author}
                          className="w-9 h-9 rounded-full object-cover border-2 border-primary"
                        />
                        <div>
                          <div className="text-sm font-semibold">
                            {testimonial.author}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {testimonial.role}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-stretch">
            {/* Integrations column */}
            <div className="h-full rounded-xl border border-border bg-card/40 p-6 sm:p-8 flex flex-col">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">
                We Connect With Your Existing Stack
              </h2>
              <p className="text-lg text-muted-foreground mb-4 max-w-xl">
                We plug directly into your website CMS and tools, so your GEO/AIO strategy fits how you already work. No heavy rebuilds, just smarter visibility.
              </p>
              <div className="flex-1 flex items-center py-2">
                <div className="grid grid-cols-3 gap-5 max-w-lg w-full">
                  <div className="h-16 rounded-lg border border-border bg-[#fafbfd] flex items-center justify-center">
                    <img
                      src="/assets/webflow.png"
                      alt="Webflow"
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="h-16 rounded-lg border border-border bg-[#fafbfd] flex items-center justify-center">
                    <img
                      src="/assets/wordpress.png"
                      alt="WordPress"
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="h-16 rounded-lg border border-border bg-[#fafbfd] flex items-center justify-center">
                    <img
                      src="/assets/shopify.png"
                      alt="Shopify"
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="h-16 rounded-lg border border-border bg-[#fafbfd] flex items-center justify-center">
                    <img
                      src="/assets/framer.png"
                      alt="Framer"
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="h-16 rounded-lg border border-border bg-[#fafbfd] flex items-center justify-center">
                    <img
                      src="/assets/react.png"
                      alt="React"
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="h-16 rounded-lg border border-border bg-[#fafbfd] flex items-center justify-center">
                    <img
                      src="/assets/next.png"
                      alt="Next.js"
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-2">
                <Button
                  variant="outline"
                  size="lg"
                  className="px-6"
                >
                  See all integrations
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Simple plans column */}
            <div className="h-full rounded-xl border border-border bg-card/40 p-6 sm:p-8 flex flex-col">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6">
                Simple Plans That Grow With You
              </h2>
              <div className="w-full max-w-lg mb-6 aspect-[32/9] rounded-lg border border-dashed border-border bg-muted/40 flex items-center justify-center text-xs text-muted-foreground">
                640 × 180 plan overview image
              </div>
              <p className="text-lg text-muted-foreground max-w-xl mb-8">
                We offer two core GEO/AIO retainers plus a third, more advanced option for larger teams. Every plan is built around increasing your visibility in AI search and turning that into real revenue.
              </p>
              <div className="mt-auto">
                <Link to="/pricing">
                  <Button size="lg" className="bg-primary text-primary-foreground shadow-primary hover:bg-primary/90">
                    See Pricing &amp; Plans
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-5" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6">
            Your Competitors Are Already Optimizing for AI Search.
            <span className="text-gradient"> Are You?</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Get a free GEO/AIO audit that other agencies typically charge <span className="font-semibold">$2,000+</span> for. We'll send you a clear report with actionable recommendations—no obligation, no pitch pressure.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="bg-primary text-primary-foreground shadow-primary hover:bg-primary/90 transition-all">
              Get My Free GEO/AIO Audit <span className="font-semibold">(Valued at $2,000+)</span>
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground mt-4">
            Delivered by email within 30 minutes • No credit card required
          </p>
        </div>
      </section>
    </div>
  );
};
